import React, { Ref, useContext, useEffect, useMemo, useState } from "react";
import Select, { CSSObjectWithLabel } from "react-select";
import { fetchTimezones, getActiveModule } from "@ts/utils";
import * as Sentry from "@sentry/react";
import { useSelector } from "react-redux";
import { mainContext } from "@ts/constants";
import {
    IMainContext,
    IValueBoolean,
    IValueString,
    ParentModule,
} from "@ts/common-types";
import { usePermissionCheck } from "@/hooks/AllCustomHooks";

interface ITimezone {
    fetchData: boolean;
    style?: Record<string, string>;
    timezoneRef?: Ref<null>;
}

type TDayPartingDisplay = {
    dayPartingDisplay: IValueBoolean;
};

type IIsDaypartingEdit = {
    isDaypartingEdit: IValueBoolean;
};

type TClientTimezone = {
    clientTimezone: IValueString;
};

const TimezonePicker: React.FC<ITimezone> = ({
    fetchData,
    style,
    timezoneRef,
}) => {
    const { timezoneChangeHandler, selectedValue } =
        useContext<IMainContext>(mainContext);
    const clientTimezone = useSelector(
        (state: TClientTimezone) => state.clientTimezone.value
    );
    const dayPartingDisplay = useSelector(
        (state: TDayPartingDisplay) => state.dayPartingDisplay.value
    );
    const isDaypartingEdit = useSelector(
        (state: IIsDaypartingEdit) => state.isDaypartingEdit.value
    );
    const [options, setOptions] = useState([]);
    const { activeModule } = getActiveModule();

    const { isModuleEditDisabled } = usePermissionCheck({ type: "cellRenderer" })

    const customStyles = {
        control: (base: CSSObjectWithLabel) => ({
            ...base,
            backgroundColor: "transparent",
            border: `1px solid ${
                activeModule === ParentModule.SETTINGS ? "#EFEFEF" : "#D8D8D8"
            }`,
            boxShadow: "none",
            "&:hover": {
                border: `1px solid ${
                    activeModule === ParentModule.SETTINGS
                        ? "#EFEFEF"
                        : "#D8D8D8"
                }`,
            },
        } as CSSObjectWithLabel),
        placeholder: (base: CSSObjectWithLabel) => {
            return {
                ...base,
                color: "black",
            } as CSSObjectWithLabel
        },
    };

    const selectedTimezone = useMemo(() => {
        return dayPartingDisplay && isDaypartingEdit
            ? selectedValue
            : clientTimezone;
    }, [dayPartingDisplay, isDaypartingEdit, selectedValue, clientTimezone]);

    useEffect(() => {
        if (fetchData) {
            fetchTimezones()
                .then((timezones) => {
                    setOptions(timezones);
                })
                .catch((err) => {
                    console.error(err);
                    Sentry.captureException(err);
                });
        }
    }, [fetchData]);

    return (
        <>
            <div style={style && { ...style }}>
                <Select
                    classNamePrefix="mySelect__value-container"
                    ref={timezoneRef}
                    placeholder={selectedTimezone}
                    openMenuOnClick={true}
                    options={options}
                    value={selectedTimezone}
                    styles={customStyles}
                    onChange={timezoneChangeHandler}
                    maxMenuHeight={430}
                    openMenuOnFocus={true}
                    components={
                        activeModule === ParentModule.SETTINGS && {
                            DropdownIndicator: () => null,
                            IndicatorSeparator: () => null,
                        }
                    }
                    isDisabled={isModuleEditDisabled}
                />
            </div>
        </>
    );
};

export default TimezonePicker;
